/**
 * What an item does in any workspace view (icon grid, cards, list): open a
 * folder at its bare path or a file through the shared entity navigation, and
 * select into the selection store over the ordered loaded ids (Shift = range,
 * Cmd/Ctrl = toggle, plain = only this one). Index-based, like the views.
 */

import { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import type { WorkspaceItem } from '@bower/shared';

import { ANALYTICS_EVENTS } from '../../../../constants/analytics';
import { ROUTES } from '../../../../constants/routes';
import { useEntityNavigation } from '../../../../hooks/entity/useEntityNavigation';
import { usePrefetchFolder } from '../../../../hooks/workspace/usePrefetchFolder';
import { phoneView } from '../../../../pages/mobile/workspace/mobileViews';
import { useSelectionStore } from '../../../../stores/selectionStore';
import { safeAnalytics } from '../../../../utils/analytics';

export interface ItemSelectModifiers {
  readonly shift: boolean;
  /** Cmd on macOS, Ctrl elsewhere. */
  readonly meta: boolean;
}

/** Where an open came from, for analytics. */
export type ItemOpenSurface = 'canvas' | 'tree' | 'action_bar' | 'mobile';

export function useItemActions(
  items: readonly WorkspaceItem[],
  surface: ItemOpenSurface = 'canvas',
) {
  const ids = useMemo(() => items.map((item) => item.id), [items]);
  const selectedIds = useSelectionStore((s) => s.selectedIds);
  const { toggle, selectOnly, selectRange } = useSelectionStore.getState();
  const navigate = useNavigate();
  // Opened AS the workspace: the artifact page keeps the workspace sidebar.
  const { openEntity } = useEntityNavigation({ fromWorkspace: true });
  const prefetch = usePrefetchFolder(surface === 'mobile' ? phoneView : undefined);
  // The layout the user is in (the canvas URL always names it).
  const [searchParams] = useSearchParams();
  const view = searchParams.get('view') ?? undefined;

  const open = useCallback(
    (index: number) => {
      const item = items[index];
      if (!item) return;
      safeAnalytics.track(ANALYTICS_EVENTS.WORKSPACE_ITEM_OPENED, {
        surface,
        view,
        item_kind: item.kind === 'folder' ? 'folder' : item.artifactType,
      });
      if (item.kind === 'folder') navigate(ROUTES.collection(item.id));
      // A link row carries its URL, so the page opens with no second fetch.
      else if (item.link) openEntity(item.artifactType, item.id, { href: item.link.url, surface });
      else openEntity(item.artifactType, item.id, { surface });
    },
    [items, navigate, openEntity, surface, view],
  );

  const select = useCallback(
    (index: number, { shift, meta }: ItemSelectModifiers) => {
      const id = ids[index];
      if (id === undefined) return;
      if (shift) selectRange(id, ids);
      else if (meta) toggle(id);
      else selectOnly(id);
    },
    [ids, selectOnly, selectRange, toggle],
  );

  return { ids, selectedIds, open, select, prefetch };
}
